import { useTheme, useMediaQuery } from '@mui/material';
import { useState, useEffect } from 'react';

// Mobile breakpoints
export const MOBILE_BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

// Hook for responsive behavior
export const useResponsive = () => {
  const theme = useTheme();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return {
    isMobile,
    isTablet,
    isDesktop,
    isSmallMobile,
    breakpoint: isMobile ? (isSmallMobile ? 'xs' : 'sm') : isTablet ? 'md' : 'lg'
  };
};

// Touch gesture detection
export const useTouchGestures = () => {
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distanceX = touchStart.x - touchEnd.x;
    const distanceY = touchStart.y - touchEnd.y;
    const isLeftSwipe = distanceX > 50;
    const isRightSwipe = distanceX < -50;
    const isUpSwipe = distanceY > 50;
    const isDownSwipe = distanceY < -50;

    return {
      isLeftSwipe,
      isRightSwipe,
      isUpSwipe,
      isDownSwipe,
      distanceX,
      distanceY,
    };
  };

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};

// Responsive grid calculations
export const getResponsiveGridProps = (isMobile: boolean, isTablet: boolean) => {
  if (isMobile) {
    return {
      tilesPerRow: 2,
      spacing: 2,
      containerPadding: 1,
      tileMinHeight: 120,
    };
  }
  
  if (isTablet) {
    return {
      tilesPerRow: 3,
      spacing: 3,
      containerPadding: 2,
      tileMinHeight: 140,
    };
  }

  return {
    tilesPerRow: 4,
    spacing: 3,
    containerPadding: 3,
    tileMinHeight: 160,
  };
};

// Mobile-specific styling
export const getMobileStyles = (isMobile: boolean) => ({
  // Header styles
  header: {
    height: isMobile ? 56 : 64,
    padding: isMobile ? '0 8px' : '0 16px',
  },
  
  // Tile styles
  tile: {
    padding: isMobile ? 12 : 16,
    fontSize: isMobile ? '0.875rem' : '1rem',
    minHeight: isMobile ? 100 : 120,
  },
  
  // Group styles
  group: {
    padding: isMobile ? 12 : 16,
    borderRadius: isMobile ? 8 : 12,
  },
  
  // Dialog styles
  dialog: {
    margin: isMobile ? 8 : 32,
    maxWidth: isMobile ? 'calc(100vw - 16px)' : 600,
    maxHeight: isMobile ? 'calc(100vh - 16px)' : 'none',
  },
  
  // Search styles
  search: {
    padding: isMobile ? '8px 16px' : '16px 24px',
    fontSize: isMobile ? '0.875rem' : '1rem',
  },
  
  // Floating action button
  fab: {
    position: 'fixed' as const,
    bottom: isMobile ? 16 : 24,
    right: isMobile ? 16 : 24,
    zIndex: 1000,
  },
});

// Responsive font sizes
export const getResponsiveFontSizes = (isMobile: boolean) => ({
  h1: isMobile ? '1.5rem' : '2rem',
  h2: isMobile ? '1.25rem' : '1.5rem',
  h3: isMobile ? '1.125rem' : '1.25rem',
  h4: isMobile ? '1rem' : '1.125rem',
  h5: isMobile ? '0.875rem' : '1rem',
  h6: isMobile ? '0.75rem' : '0.875rem',
  body1: isMobile ? '0.875rem' : '1rem',
  body2: isMobile ? '0.75rem' : '0.875rem',
  caption: isMobile ? '0.675rem' : '0.75rem',
});

// Detect if device supports touch
export const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

// Viewport utilities
export const useViewport = () => {
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return viewport;
};

// Safe area utilities for mobile devices
export const getSafeAreaInsets = () => {
  if (typeof window === 'undefined') return { top: 0, bottom: 0, left: 0, right: 0 };

  const safeAreaTop = parseInt(getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-top)') || '0');
  const safeAreaBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-bottom)') || '0');
  const safeAreaLeft = parseInt(getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-left)') || '0');
  const safeAreaRight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-right)') || '0');

  return {
    top: safeAreaTop,
    bottom: safeAreaBottom,
    left: safeAreaLeft,
    right: safeAreaRight,
  };
};

// Mobile navigation utilities
export const getMobileNavigation = (isMobile: boolean) => ({
  drawerWidth: isMobile ? '100%' : 320,
  appBarHeight: isMobile ? 56 : 64,
  bottomNavHeight: isMobile ? 56 : 0,
  fabBottomOffset: isMobile ? 72 : 24, // Account for bottom navigation
});

// Performance utilities for mobile
export const optimizeForMobile = {
  // Reduce animations on slow devices
  shouldReduceMotion: () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },
  
  // Lazy loading threshold for mobile
  getLazyLoadingThreshold: (isMobile: boolean) => isMobile ? 100 : 300,
  
  // Virtual scrolling configuration
  getVirtualScrollConfig: (isMobile: boolean) => ({
    itemHeight: isMobile ? 100 : 120,
    overscan: isMobile ? 2 : 5,
    threshold: isMobile ? 10 : 20,
  }),
};

export default {
  useResponsive,
  useTouchGestures,
  getResponsiveGridProps,
  getMobileStyles,
  getResponsiveFontSizes,
  isTouchDevice,
  useViewport,
  getSafeAreaInsets,
  getMobileNavigation,
  optimizeForMobile,
}; 